// @flow

import { type FeedbackType } from 'app/common/react/components/PinRep/feedback-types';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { type Surface } from 'app/common/react/components/PinRep/core/PinRepConfiguration';

// Never show attribution on user profile or closeup related products
export const noAttributionSurfaces = [
  'BrioCloseupRelatedProducts',
  'CloseupRelatedProducts',
  'UserProfilePinGrid',
];

/*** NOTES ON OPTION VS TYPE ***/

// AttributionOption is a knob available to devs to suggest if a PinRep should show an attribution,
// and if so, what type. However, that option can be overridden in certain circumstances.

// AttributionType is what gets passed down to the footer to determine what is displayed.
// It is derived in this function, and will not be overridden.

export type AttributionOption = 'show' | 'image-only' | 'hide';

// Pins can have three different styles of attribution:
// default - normal username/avatar attribution
// image-only - text such as "picked for you" or topic attribution
// none - no attribution is shown
export type AttributionType = 'default' | 'image-only-credit' | 'none';

type GetAttributionTypeArgs = {|
  pin: Pin,

  // See note above about attributionOption
  attributionOption?: ?AttributionOption,

  // indicates why a pin is in a given grid
  feedbackType: FeedbackType,

  // Is this being shown on a group board?
  isGroupBoard: boolean,

  // showImageOnly allows us to limit controls and descriptions for NUX.
  showImageOnly: boolean,

  // Which grid is displaying this PinRep?
  surface?: Surface,
|};

// Derive whether or not we should show attribution from Pin data. If the
// surface overrides this value (for example, Following Feed always shows
// attribution), use the override, except for promoted pins.
export default function getAttributionType({
  attributionOption,
  feedbackType,
  isGroupBoard,
  pin,
  showImageOnly,
  surface,
}: GetAttributionTypeArgs): AttributionType {
  const { is_downstream_promotion: isDownstreamPromotion, promoter } = pin;

  // This exists above the forced override. Legal requirement: promoted pins
  // must *always* show a "Promoted by" attribution.
  if (feedbackType === 'promoted' || (promoter && !isDownstreamPromotion)) {
    return 'default';
  }

  if (noAttributionSurfaces.includes(surface)) {
    return 'none';
  }

  // Never show attribution on group boards.
  if (isGroupBoard) {
    return 'none';
  }

  let attributionType = 'default';

  // Option specified at the grid level
  if (attributionOption) {
    if (attributionOption === 'image-only') {
      attributionType = 'image-only-credit';
    } else if (attributionOption === 'hide') {
      attributionType = 'none';
    }
  }

  if (feedbackType === 'followed' && !showImageOnly) {
    return attributionType;
  }

  return 'none';
}
